import { useApiQuery } from "../use-api-query";

/**
 * Transcript payload returned by
 * `GET /client/argus/sessions/{id}/transcript`.
 *
 * - `messages`: opaque event list — the gateway returns the reduced
 *   `SessionContext.messages` shape when the live path succeeds; the S3
 *   fallback returns raw JSONL events. The FE treats both as
 *   `ArgusMessage[]` via the existing event reducer. See
 *   `be/api/argus/client.py::_parse_jsonl_to_messages` for the parsing
 *   risk note.
 * - `read_only`: true when the transcript was served from S3 archive
 *   (live gateway was unavailable or the sandbox is gone). The UI
 *   disables the composer and shows a "This session has ended." banner.
 */
export interface SessionTranscript {
  messages: unknown[];
  read_only: boolean;
}

interface SessionTranscriptEnvelope {
  status?: string;
  message?: string;
  data: SessionTranscript;
}

/**
 * Fetches a session transcript via the cope-api proxy route.
 *
 * Matches the retry/backoff semantics of the previous direct-gateway
 * fetch in `use-argus-connection.ts:360-409` (4 attempts, 400 → 800 →
 * 1600 ms), because the live path still needs resilience during gateway
 * boot races right after /update or /resume.
 */
export function useArgusSessionTranscript(sessionId: string | null) {
  const query = useApiQuery<SessionTranscriptEnvelope>(
    `/client/argus/sessions/${sessionId}/transcript`,
    {
      enabled: !!sessionId,
      retry: 4,
      retryDelay: (attempt: number) => Math.min(400 * 2 ** attempt, 1600),
      staleTime: 30_000,
      // No periodic refetch — transcripts are pulled on-demand when the
      // active session changes. The default 2s interval from useApiQuery
      // would thrash the gateway/S3 for no benefit.
      refetchInterval: false,
      refetchOnWindowFocus: false,
    },
  );

  return {
    ...query,
    transcript: query.data?.data,
    refetch: query.refetch,
  };
}
